// All application destinations are resolved here before any dev UI is mounted.
export const environmentName = String(process.env.REACT_APP_CHEMCAB_ENV || 'production').trim().toLowerCase();
if (!['production', 'dev', 'local'].includes(environmentName)) {
  throw new Error('CHEMCAB_ENV must be production, dev, or local.');
}
export const isDev = environmentName !== 'production';

function setting(name, value, productionDefault = '') {
  const result = String(value || (isDev ? '' : productionDefault)).trim();
  if (isDev && !result) throw new Error(`Missing dev configuration: ${name}`);
  return result;
}

function destination(name, value, productionDefault = '') {
  const result = setting(name, value, productionDefault).replace(/\/+$/, '');
  if (!isDev) return result;
  const url = new URL(result);
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.username || url.password || url.search || url.hash ||
      (url.protocol !== 'https:' && !(local && url.protocol === 'http:'))) {
    throw new Error(`Invalid dev URL: ${name}`);
  }
  if (environmentName === 'local' && !local) throw new Error(`Local profile requires a loopback URL: ${name}`);
  if (url.hostname === 'chemcab.com' || url.hostname.endsWith('.chemcab.com') ||
      /chemcab-prod|763233338110|prod(uction)?[.-]/i.test(url.hostname)) {
    throw new Error(`Production URL forbidden in dev: ${name}`);
  }
  return result;
}

export const projectId = setting('FIREBASE_PROJECT_ID', process.env.REACT_APP_FIREBASE_PROJECT_ID, 'chemcab-prod');
if (isDev && (/prod(uction)?/i.test(projectId) || !/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(projectId))) {
  throw new Error('Dev requires a separate non-production Firebase project ID.');
}
if (environmentName === 'local' && !projectId.startsWith('demo-')) {
  throw new Error('Local profile requires a demo- Firebase project ID.');
}
export const apiBase = destination('API_BASE', process.env.REACT_APP_BACKEND_URL, 'https://chemcab-push-listener-763233338110.us-central1.run.app');
export const portalUrl = destination('PORTAL_URL', process.env.REACT_APP_PORTAL_URL, 'https://portal.chemcab.com');

export async function verifyDevEnvironment() {
  if (!isDev) return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(`${apiBase}/api/environment`, {
      cache: 'no-store', signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Environment check failed (${response.status}).`);
    const descriptor = await response.json();
    if (descriptor.environment !== environmentName || descriptor.projectId !== projectId ||
        descriptor.integrationMode !== 'simulated') {
      throw new Error('Dev API identity mismatch. App access is disabled.');
    }
  } finally {
    clearTimeout(timeout);
  }
}

export function showDevIndicator() {
  if (!isDev) return;
  const badge = document.createElement('div');
  badge.textContent = `${environmentName.toUpperCase()} · ${projectId} · Simulated`;
  badge.setAttribute('role', 'status');
  Object.assign(badge.style, {
    position: 'fixed', top: '0', left: '0', right: '0', zIndex: '2147483647',
    padding: '5px 12px', textAlign: 'center', color: '#241a00',
    background: '#ffd166', font: '700 12px/1.4 system-ui', pointerEvents: 'none',
  });
  document.body.style.paddingTop = '28px';
  document.body.appendChild(badge);
  document.title = `[DEV] ${document.title}`;
}
